import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  TextField,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  MenuItem,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  InputAdornment,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';

// Price type options
const priceTypes = [
  { value: 'fixed', label: 'Фиксированная' },
  { value: 'hourly', label: 'Почасовая' },
  { value: 'daily', label: 'Посуточная' }
];

// Helper to normalize image URLs
const getFullImageUrl = (url) => {
  if (!url) return '/images/placeholder-service.jpg';

  // Absolute URLs and data URIs stay as-is
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url;
  }

  // Already root-relative (/images/..., /uploads/...)
  if (url.startsWith('/')) {
    return url;
  }

  // Fallback: ensure leading slash for relative paths
  return `/${url}`;
};

// TabPanel component for tab content
const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`service-tabpanel-${index}`}
      aria-labelledby={`service-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

// Image upload component
const ImageUpload = ({
  mainImagePreview,
  additionalImagePreviews,
  existingImages,
  handleMainImageChange,
  handleAdditionalImagesChange,
  handleRemoveAdditionalImage,
  handleRemoveExistingImage,
  handleUpdateCaption,
  handleUpdateExistingCaption,
  isEditMode
}) => {
  return (
    <Grid container spacing={3} sx={{ mt: 2 }}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Изображения
        </Typography>
        <Divider />
      </Grid>
      
      {/* Main image upload */}
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" gutterBottom>
          Главное изображение
        </Typography>
        <input
          accept="image/*"
          type="file"
          id="main-image-upload"
          style={{ display: 'none' }}
          onChange={handleMainImageChange}
        />
        <label htmlFor="main-image-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={<ImageIcon />}
            sx={{ mb: 2 }}
          >
            Выбрать главное изображение
          </Button>
        </label>
        
        {console.log('Отображение главного изображения:', mainImagePreview)}
        {mainImagePreview && (
          <Card sx={{ maxWidth: 345, mb: 2 }}>
            <CardMedia
              component="img"
              height="200"
              image={getFullImageUrl(mainImagePreview)}
              alt="Главное изображение"
              sx={{ objectFit: 'cover' }}
              onError={(e) => {
                console.error('Ошибка загрузки главного изображения:', e);
                e.target.src = '/images/placeholder-service.jpg';
              }}
            />
          </Card>
        )}
      </Grid>
      
      {/* Additional images upload */}
      <Grid item xs={12} md={6}>
        <Typography variant="subtitle1" gutterBottom>
          Дополнительные изображения
        </Typography>
        <input
          accept="image/*"
          type="file"
          multiple
          id="additional-images-upload"
          style={{ display: 'none' }}
          onChange={handleAdditionalImagesChange}
        />
        <label htmlFor="additional-images-upload">
          <Button
            variant="outlined"
            component="span"
            startIcon={<ImageIcon />}
            sx={{ mb: 2 }}
          >
            Добавить изображения
          </Button>
        </label>
        
        <Grid container spacing={2}>
          {additionalImagePreviews.map((preview, index) => (
            <Grid item xs={6} sm={4} key={`new-${index}`}>
              <Card>
                <CardMedia
                  component="img"
                  height="140"
                  image={getFullImageUrl(preview.url)}
                  alt={`Изображение ${index + 1}`}
                  sx={{ objectFit: 'cover' }}
                />
                <CardActions>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Подпись"
                    value={preview.caption || ''}
                    onChange={(e) => {
                      const newPreviews = [...additionalImagePreviews];
                      newPreviews[index].caption = e.target.value;
                      handleUpdateCaption(index, e.target.value);
                    }}
                    sx={{ mb: 1 }}
                  />
                  <IconButton 
                    color="error" 
                    onClick={() => handleRemoveAdditionalImage(index)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
          
          {isEditMode && existingImages.map((image, index) => (
            <Grid item xs={6} sm={4} key={`existing-${image.id}`}>
              <Card>
                <CardMedia
                  component="img"
                  height="140"
                  image={getFullImageUrl(image.imageUrl)}
                  alt={image.caption || `Изображение ${index + 1}`}
                  sx={{ objectFit: 'cover' }}
                />
                <CardActions>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Подпись"
                    value={image.caption || ''}
                    onChange={(e) => handleUpdateExistingCaption(image.id, e.target.value)}
                    sx={{ mb: 1 }}
                  />
                  <IconButton 
                    color="error" 
                    onClick={() => handleRemoveExistingImage(image.id)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Grid>
    </Grid>
  );
};

ImageUpload.propTypes = {
  mainImagePreview: PropTypes.string,
  additionalImagePreviews: PropTypes.array.isRequired,
  existingImages: PropTypes.array.isRequired,
  handleMainImageChange: PropTypes.func.isRequired,
  handleAdditionalImagesChange: PropTypes.func.isRequired,
  handleRemoveAdditionalImage: PropTypes.func.isRequired,
  handleRemoveExistingImage: PropTypes.func.isRequired,
  handleUpdateCaption: PropTypes.func.isRequired,
  handleUpdateExistingCaption: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
};

// Video upload component
const VideoUpload = ({
  videos,
  existingVideos,
  handleVideoUrlChange,
  handleAddVideo,
  handleRemoveVideo,
  handleRemoveExistingVideo,
  availableVideos,
  handleSelectExistingVideo,
  selectedVideos
}) => {
  const [videoUrl, setVideoUrl] = useState('');
  const [videoCaption, setVideoCaption] = useState('');
  const [openVideoSelector, setOpenVideoSelector] = useState(false);

  const handleAddVideoClick = () => {
    if (videoUrl) {
      handleAddVideo({ videoUrl: videoUrl, caption: videoCaption });
      setVideoUrl('');
      setVideoCaption('');
    }
  };

  return (
    <Grid container spacing={3} sx={{ mt: 2 }}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Видео
        </Typography>
        <Divider />
      </Grid>
      
      {/* Video selection buttons */}
      <Grid item xs={12} container spacing={2}>
        <Grid item xs={12} md={6}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<VideocamIcon />}
            sx={{ height: '56px' }}
            onClick={() => setOpenVideoSelector(true)}
          >
            Выбрать существующие видео
          </Button>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <TextField
              label="URL видео (YouTube, Vimeo)"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              fullWidth
            />
            <TextField
              label="Подпись"
              value={videoCaption}
              onChange={(e) => setVideoCaption(e.target.value)}
              fullWidth
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddVideoClick}
              disabled={!videoUrl}
            >
              Добавить
            </Button>
          </Box>
        </Grid>
      </Grid>
      
      {/* Video selection dialog */}
      <Dialog 
        open={openVideoSelector} 
        onClose={() => setOpenVideoSelector(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Выберите видео для услуги</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            {availableVideos && availableVideos.length > 0 ? (
              availableVideos.map((video) => {
                const isSelected = selectedVideos?.some(v => v.id === video.id);
                return (
                  <Grid item xs={12} sm={6} md={4} key={`video-${video.id}`}>
                    <Card 
                      sx={{
                        border: isSelected ? '2px solid #3498db' : 'none',
                        position: 'relative'
                      }}
                    >
                      {isSelected && (
                        <Chip 
                          label="Выбрано" 
                          color="primary" 
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            zIndex: 1
                          }}
                        />
                      )}
                      <CardMedia
                        component="video"
                        height="200"
                        src={video.url || video.videoUrl}
                        poster={video.thumbnailUrl || video.thumbnail || undefined}
                        controls
                      />
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          {video.title || video.caption || 'Без названия'}
                        </Typography>
                        {video.description && (
                          <Typography variant="body2" color="textSecondary" noWrap>
                            {video.description}
                          </Typography>
                        )}
                        <Typography variant="caption" color="primary" display="block" sx={{ mt: 1 }}>
                          ID: {video.id}
                        </Typography>
                      </CardContent>
                      <CardActions>
                        <Button 
                          size="small" 
                          color={isSelected ? "error" : "primary"}
                          onClick={() => handleSelectExistingVideo(video, isSelected)}
                        >
                          {isSelected ? 'Удалить' : 'Выбрать'}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                );
              })
            ) : (
              <Grid item xs={12}>
                <Typography variant="body1" align="center">
                  Нет доступных видео
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVideoSelector(false)} color="primary">
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Selected videos */}
      {selectedVideos && selectedVideos.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Выбранные видео:
          </Typography>
          <Grid container spacing={2}>
            {selectedVideos.map((video) => (
              <Grid item xs={12} sm={6} md={4} key={`selected-video-${video.id}`}>
                <Card>
                  <CardMedia
                    component="video"
                    height="200"
                    src={video.url || video.videoUrl}
                    poster={video.thumbnailUrl || video.thumbnail || undefined}
                    controls
                  />
                  <CardContent>
                    <Typography variant="subtitle2">
                      {video.title || video.caption || 'Без названия'}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <Button 
                      size="small" 
                      color="error"
                      onClick={() => handleSelectExistingVideo(video, true)}
                    >
                      Удалить
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
      
      {/* New videos */}
      {videos && videos.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Новые видео:
          </Typography>
          <Grid container spacing={2}>
            {videos.map((video, index) => (
              <Grid item xs={12} sm={6} md={4} key={`new-video-${index}`}>
                <Card>
                  <CardContent>
                    <Typography variant="subtitle1" noWrap>
                      {video.url}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {video.caption || 'Без подписи'}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <IconButton 
                      color="error" 
                      onClick={() => handleRemoveVideo(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
      
      {/* Existing videos */}
      {existingVideos && existingVideos.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Существующие видео:
          </Typography>
          <Grid container spacing={2}>
            {existingVideos.map((video) => (
              <Grid item xs={12} sm={6} md={4} key={`existing-video-${video.id}`}>
                <Card>
                  <CardMedia
                    component="video"
                    height="200"
                    src={video.videoUrl}
                    poster={video.thumbnailUrl || undefined}
                    controls
                  />
                  <CardContent>
                    <Typography variant="subtitle1" noWrap>
                      {video.caption || 'Без подписи'}
                    </Typography>
                  </CardContent>
                  <CardActions>
                    <IconButton 
                      color="error" 
                      onClick={() => handleRemoveExistingVideo(video.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

VideoUpload.propTypes = {
  videos: PropTypes.array.isRequired,
  existingVideos: PropTypes.array.isRequired,
  handleVideoUrlChange: PropTypes.func.isRequired,
  handleAddVideo: PropTypes.func.isRequired,
  handleRemoveVideo: PropTypes.func.isRequired,
  handleRemoveExistingVideo: PropTypes.func.isRequired,
};

// Custom Prices component
const CustomPrices = ({
  customPrices,
  existingCustomPrices,
  handleAddCustomPrice,
  handleRemoveCustomPrice,
  handleUpdateCustomPrice,
  handleRemoveExistingCustomPrice,
  handleUpdateExistingCustomPrice
}) => {
  return (
    <Grid container spacing={3} sx={{ mt: 2 }}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Кастомные цены
        </Typography>
        <Divider />
      </Grid>
      
      <Grid item xs={12}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddCustomPrice}
          sx={{ mb: 2 }}
        >
          Добавить кастомную цену
        </Button>
      </Grid>
      
      {/* New custom prices */}
      {customPrices.map((price, index) => (
        <Grid item xs={12} key={`new-price-${index}`} sx={{ mb: 2 }}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Название"
                  fullWidth
                  value={price.name}
                  onChange={(e) => handleUpdateCustomPrice(index, 'name', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Цена"
                  fullWidth
                  type="number"
                  value={price.price}
                  onChange={(e) => handleUpdateCustomPrice(index, 'price', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">₽</InputAdornment>,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Описание"
                  fullWidth
                  value={price.description || ''}
                  onChange={(e) => handleUpdateCustomPrice(index, 'description', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={1}>
                <IconButton 
                  color="error" 
                  onClick={() => handleRemoveCustomPrice(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      ))}
      
      {/* Existing custom prices */}
      {existingCustomPrices.map((price) => (
        <Grid item xs={12} key={`existing-price-${price.id}`} sx={{ mb: 2 }}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Название"
                  fullWidth
                  value={price.name}
                  onChange={(e) => handleUpdateExistingCustomPrice(price.id, 'name', e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Цена"
                  fullWidth
                  type="number"
                  value={price.price}
                  onChange={(e) => handleUpdateExistingCustomPrice(price.id, 'price', e.target.value)}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">₽</InputAdornment>,
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Описание"
                  fullWidth
                  value={price.description || ''}
                  onChange={(e) => handleUpdateExistingCustomPrice(price.id, 'description', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={1}>
                <IconButton 
                  color="error" 
                  onClick={() => handleRemoveExistingCustomPrice(price.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

CustomPrices.propTypes = {
  customPrices: PropTypes.array.isRequired,
  existingCustomPrices: PropTypes.array.isRequired,
  handleAddCustomPrice: PropTypes.func.isRequired,
  handleRemoveCustomPrice: PropTypes.func.isRequired,
  handleUpdateCustomPrice: PropTypes.func.isRequired,
  handleRemoveExistingCustomPrice: PropTypes.func.isRequired,
  handleUpdateExistingCustomPrice: PropTypes.func.isRequired,
};

// Form actions component
const FormActions = ({ onCancel, onSubmit, isSubmitting, isEditMode }) => {
  return (
    <Grid container spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
      <Grid item>
        <Button
          variant="outlined"
          onClick={onCancel}
          startIcon={<ArrowBackIcon />}
          disabled={isSubmitting}
        >
          Отмена
        </Button>
      </Grid>
      <Grid item>
        <Button
          variant="contained"
          color="primary"
          onClick={onSubmit}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isEditMode ? 'Сохранить изменения' : 'Создать услугу'}
        </Button>
      </Grid>
    </Grid>
  );
};

FormActions.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  isEditMode: PropTypes.bool.isRequired,
};

const ServiceForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  
  const { register, control, handleSubmit, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      description: '',
      priceType: 'fixed',
      basePrice: '',
      hourlyPrice: '',
      dailyPrice: '',
      details: '',
      metaTitle: '',
      metaDescription: '',
      metaKeywords: '',
      isActive: true
    }
  });
  
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(isEditMode);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Image state
  const [mainImage, setMainImage] = useState(null);
  const [mainImagePreview, setMainImagePreview] = useState('');
  const [additionalImages, setAdditionalImages] = useState([]);
  const [additionalImagePreviews, setAdditionalImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [mainImageFile, setMainImageFile] = useState(null);
  const [additionalImageFiles, setAdditionalImageFiles] = useState([]);
  
  // State for videos
  const [videos, setVideos] = useState([]);
  const [existingVideos, setExistingVideos] = useState([]);
  const [availableVideos, setAvailableVideos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  
  // Custom prices state
  const [customPrices, setCustomPrices] = useState([]);
  const [existingCustomPrices, setExistingCustomPrices] = useState([]);
  const [customPricesToDelete, setCustomPricesToDelete] = useState([]);
  
  const priceType = watch('priceType');
  
  // Fetch service data if in edit mode
  useEffect(() => {
    const fetchService = async () => {
      if (!isEditMode) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/services/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        const service = response.data;
        
        // Set form values
        setValue('name', service.name);
        setValue('description', service.description || '');
        setValue('priceType', service.priceType);
        setValue('basePrice', service.basePrice || '');
        setValue('hourlyPrice', service.hourlyPrice || '');
        setValue('dailyPrice', service.dailyPrice || '');
        // Обрабатываем поле details, которое может быть JSON объектом
        if (service.details) {
          if (typeof service.details === 'object' && service.details.text) {
            setValue('details', service.details.text);
          } else if (typeof service.details === 'string') {
            try {
              const detailsObj = JSON.parse(service.details);
              setValue('details', detailsObj.text || '');
            } catch (e) {
              setValue('details', service.details);
            }
          } else {
            setValue('details', '');
          }
        } else {
          setValue('details', '');
        }
        setValue('metaTitle', service.metaTitle || '');
        setValue('metaDescription', service.metaDescription || '');
        setValue('metaKeywords', service.metaKeywords || '');
        setValue('isActive', service.isActive);
        
        // Set main image preview
        if (service.featuredImage) {
          console.log('Загружено главное изображение услуги:', service.featuredImage);
          setMainImagePreview(service.featuredImage);
        } else {
          console.log('Главное изображение отсутствует');
        }
        
        // Fetch additional images
        const imagesResponse = await axios.get(`/api/services/${id}/images`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setExistingImages(imagesResponse.data);
        
        // Fetch videos
        const videosResponse = await axios.get(`/api/services/${id}/videos`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setExistingVideos(videosResponse.data);
        
        // Fetch custom prices
        const customPricesResponse = await axios.get(`/api/services/${id}/custom-prices`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setExistingCustomPrices(customPricesResponse.data);
        console.log('Загружены кастомные цены:', customPricesResponse.data);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching service:', error);
        setError('Ошибка при загрузке данных услуги');
        setLoading(false);
      }
    };
    
    fetchService();
  }, [id, isEditMode, setValue]);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  // Handle main image change
  const handleMainImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setMainImage(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setMainImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle additional images change
  const handleAdditionalImagesChange = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files);
      setAdditionalImages([...additionalImages, ...files]);
      
      const newPreviews = [];
      
      files.forEach(file => {
        const reader = new FileReader();
        reader.onload = (e) => {
          newPreviews.push({
            url: e.target.result,
            file: file,
            caption: ''
          });
          
          if (newPreviews.length === files.length) {
            setAdditionalImagePreviews([...additionalImagePreviews, ...newPreviews]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };
  
  // Handle remove additional image
  const handleRemoveAdditionalImage = (index) => {
    const newImages = [...additionalImages];
    newImages.splice(index, 1);
    setAdditionalImages(newImages);
    
    const newPreviews = [...additionalImagePreviews];
    newPreviews.splice(index, 1);
    setAdditionalImagePreviews(newPreviews);
  };
  
  // Handle remove existing image
  const handleRemoveExistingImage = (imageId) => {
    try {
      setExistingImages(existingImages.filter(img => img.id !== imageId));
      setImagesToDelete([...imagesToDelete, imageId]);
    } catch (error) {
      console.error('Error removing existing image:', error);
      setError('Ошибка при удалении изображения');
    }
  };
  
  // Handle update caption for additional images
  const handleUpdateCaption = (index, caption) => {
    try {
      const newPreviews = [...additionalImagePreviews];
      newPreviews[index].caption = caption;
      setAdditionalImagePreviews(newPreviews);
    } catch (error) {
      console.error('Error updating image caption:', error);
      setError('Ошибка при обновлении подписи к изображению');
    }
  };
  
  // Handle update caption for existing image
  const handleUpdateExistingCaption = (imageId, caption) => {
    setExistingImages(prevImages =>
      prevImages.map(img => img.id === imageId ? { ...img, caption } : img)
    );
  };
  
  // Video functions
  
  // Загрузка доступных видео для выбора
  useEffect(() => {
    if (tabValue === 0) { // Загружаем видео только при активной вкладке "Основное"
      loadAvailableVideos();
    }
  }, [tabValue]);
  
  // Загружаем видео при первой загрузке компонента
  useEffect(() => {
    loadAvailableVideos();
  }, []);
  
  // Handle selecting an existing video from the library
  const handleSelectExistingVideo = (video, isSelected) => {
    if (isSelected) {
      // Remove from selected videos
      setSelectedVideos(selectedVideos.filter(v => v.id !== video.id));
    } else {
      // Add to selected videos
      setSelectedVideos([...selectedVideos, video]);
    }
  };
  
  // Handle removing an existing video
  const handleRemoveExistingVideo = async (videoId) => {
    try {
      const token = localStorage.getItem('token');
      
      // Only make API call if we're in edit mode
      if (isEditMode) {
        // Используем правильный URL согласно API эндпоинту на сервере
        await axios.delete(`/api/services/videos/${videoId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      }
      
      // Update state to remove the video
      setExistingVideos(existingVideos.filter(video => video.id !== videoId));
    } catch (error) {
      console.error('Error removing existing video:', error);
      setError('Ошибка при удалении видео');
    }
  };
  
  // Load available videos for selection
  const loadAvailableVideos = async () => {
    try {
      console.log('Загрузка доступных видео...');
      const response = await axios.get('/api/videos');
      console.log('Ответ от API:', response.data);
      
      // Проверяем, что ответ содержит поле data с массивом видео
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        console.log('Найдено видео:', response.data.data.length);
        setAvailableVideos(response.data.data);
      } else {
        console.warn('Неправильный формат данных:', response.data);
        setAvailableVideos([]);
      }
    } catch (error) {
      console.error('Ошибка загрузки доступных видео:', error);
    }
  };
  
  // Handle adding a new custom price
  const handleAddCustomPrice = () => {
    setCustomPrices(prev => [...prev, { name: '', price: '', description: '' }]);
  };
  
  // Handle removing a new custom price
  const handleRemoveCustomPrice = (index) => {
    setCustomPrices(prev => prev.filter((_, i) => i !== index));
  };
  
  // Handle updating a new custom price
  const handleUpdateCustomPrice = (index, field, value) => {
    setCustomPrices(prev => 
      prev.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    );
  };
  
  // Handle removing an existing custom price
  const handleRemoveExistingCustomPrice = (priceId) => {
    setExistingCustomPrices(prev => prev.filter(price => price.id !== priceId));
    setCustomPricesToDelete(prev => [...prev, priceId]);
  };
  
  // Handle updating an existing custom price
  const handleUpdateExistingCustomPrice = (priceId, field, value) => {
    setExistingCustomPrices(prev => 
      prev.map(price => 
        price.id === priceId ? { ...price, [field]: value } : price
      )
    );
  };
  
  // Handle add video
  // Handle adding a new video
  const handleAddVideo = (newVideo) => {
    setVideos([...videos, newVideo]);
  };
  
  // Handle removing a new video
  const handleRemoveVideo = (index) => {
    setVideos(videos.filter((_, i) => i !== index));
  };
  
  // Функция handleRemoveExistingVideo перенесена выше для избежания дублирования
  
  // Handle form submission
  const onSubmit = async (data) => {
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(null);
      
      // Форматируем поле details как JSON объект с полем text
      const formattedData = {
        ...data,
        details: data.details ? { text: data.details } : null
      };
      
      const token = localStorage.getItem('token');
      let serviceId = id;
      
      // Create or update service
      if (isEditMode) {
        await axios.put(`/api/services/${id}`, formattedData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
      } else {
        const response = await axios.post('/api/services', formattedData, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        serviceId = response.data.id;
      }
      
      // Upload main image if changed
      if (mainImage) {
        const formData = new FormData();
        formData.append('featuredImage', mainImage); // Используем правильное имя поля
        
        await axios.post(`/api/services/${serviceId}/featured-image`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        });
      }
      
      // Upload additional images
      if (additionalImages.length > 0) {
        for (let i = 0; i < additionalImages.length; i++) {
          const formData = new FormData();
          formData.append('image', additionalImages[i]);
          formData.append('caption', additionalImagePreviews[i].caption || '');
          
          await axios.post(`/api/services/${serviceId}/images`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`
            }
          });
        }
      }
      
      // Update captions for existing images
      if (isEditMode && existingImages.length > 0) {
        for (const image of existingImages) {
          // Только если изображение имеет подпись и не помечено для удаления
          if (image.caption !== undefined && !imagesToDelete.includes(image.id)) {
            await axios.put(`/api/services/images/${image.id}`, {
              caption: image.caption
            }, {
              headers: {
                Authorization: `Bearer ${token}`
              }
            });
          }
        }
      }
      
      // Delete images marked for deletion
      if (imagesToDelete.length > 0) {
        for (const imageId of imagesToDelete) {
          await axios.delete(`/api/services/images/${imageId}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        }
      }
      
      // Add videos
      if (videos.length > 0) {
        for (const video of videos) {
          await axios.post(`/api/services/${serviceId}/videos`, video, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        }
      }
      
      // Добавляем выбранные видео из библиотеки
      if (selectedVideos && selectedVideos.length > 0) {
        for (const video of selectedVideos) {
          // Используем существующий эндпоинт для добавления видео
          await axios.post(`/api/services/${serviceId}/videos`, {
            videoUrl: video.url || video.videoUrl,
            caption: video.caption || video.title || '',
            videoId: video.id
          }, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        }
      }
      
      // Add new custom prices
      if (customPrices.length > 0) {
        for (const price of customPrices) {
          await axios.post(`/api/services/${serviceId}/custom-prices`, price, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        }
      }
      
      // Update existing custom prices
      if (existingCustomPrices.length > 0) {
        for (const price of existingCustomPrices) {
          await axios.put(`/api/services/custom-prices/${price.id}`, {
            name: price.name,
            price: price.price,
            description: price.description || ''
          }, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        }
      }
      
      // Delete custom prices marked for deletion
      if (customPricesToDelete.length > 0) {
        for (const priceId of customPricesToDelete) {
          await axios.delete(`/api/services/custom-prices/${priceId}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
        }
      }
      
      setSubmitting(false);
      setSuccess('Услуга успешно сохранена');
      
      // Redirect to services list
      setTimeout(() => {
        navigate('/admin/services');
      }, 1500);
    } catch (error) {
      console.error('Error saving service:', error);
      setError('Ошибка при сохранении услуги');
      setSubmitting(false);
    }
  };
  
  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/services')}
        >
          Назад к списку услуг
        </Button>
      </Box>
      
      <Typography variant="h5" component="h1" gutterBottom>
        {isEditMode ? 'Редактирование услуги' : 'Добавление новой услуги'}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper elevation={2} sx={{ p: 3 }}>
          <form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab label="Основная информация" />
                  <Tab label="SEO и метаданные" />
                </Tabs>
              </Grid>
              
              {/* Basic info tab */}
              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="Название услуги"
                      fullWidth
                      required
                      {...register('name', { required: 'Название обязательно' })}
                      error={Boolean(errors.name)}
                      helperText={errors.name?.message}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      label="Описание"
                      fullWidth
                      multiline
                      rows={4}
                      {...register('description')}
                    />
                  </Grid>
                  
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth required>
                      <InputLabel>Тип цены</InputLabel>
                      <Controller
                        name="priceType"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            label="Тип цены"
                          >
                            {priceTypes.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Grid>
                  
                  {priceType === 'fixed' && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Цена"
                        fullWidth
                        type="number"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">₽</InputAdornment>,
                        }}
                        {...register('basePrice', { 
                          required: priceType === 'fixed' ? 'Цена обязательна' : false 
                        })}
                        error={Boolean(errors.basePrice)}
                        helperText={errors.basePrice?.message}
                      />
                    </Grid>
                  )}
                  
                  {priceType === 'hourly' && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Цена за час"
                        fullWidth
                        type="number"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">₽/час</InputAdornment>,
                        }}
                        {...register('hourlyPrice', { 
                          required: priceType === 'hourly' ? 'Цена за час обязательна' : false 
                        })}
                        error={Boolean(errors.hourlyPrice)}
                        helperText={errors.hourlyPrice?.message}
                      />
                    </Grid>
                  )}
                  
                  {priceType === 'daily' && (
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Цена за день"
                        fullWidth
                        type="number"
                        InputProps={{
                          endAdornment: <InputAdornment position="end">₽/день</InputAdornment>,
                        }}
                        {...register('dailyPrice', { 
                          required: priceType === 'daily' ? 'Цена за день обязательна' : false 
                        })}
                        error={Boolean(errors.dailyPrice)}
                        helperText={errors.dailyPrice?.message}
                      />
                    </Grid>
                  )}
                  
                  {/* Custom Prices */}
                  <Grid item xs={12}>
                    <CustomPrices
                      customPrices={customPrices}
                      existingCustomPrices={existingCustomPrices}
                      handleAddCustomPrice={handleAddCustomPrice}
                      handleRemoveCustomPrice={handleRemoveCustomPrice}
                      handleUpdateCustomPrice={handleUpdateCustomPrice}
                      handleRemoveExistingCustomPrice={handleRemoveExistingCustomPrice}
                      handleUpdateExistingCustomPrice={handleUpdateExistingCustomPrice}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      label="Дополнительная информация"
                      fullWidth
                      multiline
                      rows={4}
                      {...register('details')}
                      helperText="Дополнительная информация об услуге (опционально)"
                    />
                  </Grid>
                  
                  {/* Images */}
                  <Grid item xs={12}>
                    <ImageUpload 
                      mainImagePreview={mainImagePreview}
                      additionalImagePreviews={additionalImagePreviews}
                      existingImages={existingImages}
                      handleMainImageChange={handleMainImageChange}
                      handleAdditionalImagesChange={handleAdditionalImagesChange}
                      handleRemoveAdditionalImage={handleRemoveAdditionalImage}
                      handleRemoveExistingImage={handleRemoveExistingImage}
                      handleUpdateCaption={handleUpdateCaption}
                      handleUpdateExistingCaption={handleUpdateExistingCaption}
                      isEditMode={isEditMode}
                    />
                  </Grid>
                  
                  {/* Videos */}
                  <Grid item xs={12}>
                    <VideoUpload 
                      videos={videos}
                      existingVideos={existingVideos}
                      handleVideoUrlChange={() => {}}
                      handleAddVideo={handleAddVideo}
                      handleRemoveVideo={handleRemoveVideo}
                      handleRemoveExistingVideo={handleRemoveExistingVideo}
                      availableVideos={availableVideos}
                      handleSelectExistingVideo={handleSelectExistingVideo}
                      selectedVideos={selectedVideos}
                    />
                  </Grid>
                  
                  {/* Active switch */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Controller
                          name="isActive"
                          control={control}
                          render={({ field }) => (
                            <Switch
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              color="primary"
                            />
                          )}
                        />
                      }
                      label="Активная"
                    />
                  </Grid>
                </Grid>
              </TabPanel>
              
              {/* SEO tab */}
              <TabPanel value={tabValue} index={1}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      label="Meta Title"
                      fullWidth
                      {...register('metaTitle')}
                      helperText="SEO заголовок для страницы услуги"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      label="Meta Description"
                      fullWidth
                      multiline
                      rows={3}
                      {...register('metaDescription')}
                      helperText="SEO описание для страницы услуги"
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      label="Meta Keywords"
                      fullWidth
                      {...register('metaKeywords')}
                      helperText="Ключевые слова через запятую"
                    />
                  </Grid>
                </Grid>
              </TabPanel>
              
              {/* Form actions */}
              <Grid item xs={12}>
                <FormActions 
                  onCancel={() => navigate('/admin/services')} 
                  onSubmit={handleSubmit(onSubmit)} 
                  isSubmitting={submitting} 
                  isEditMode={isEditMode} 
                />
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}
    </Box>
  );
};

export default ServiceForm;
